import React, { useCallback, useState } from 'react';
import { useOverflowMeasure } from './useOverflowMeasure';

// Scroll positions can overshoot their extent on elastic scroll, so the ratio
// is clamped before it becomes a dot index.
const clampFraction = (n: number): number => Math.min(1, Math.max(0, n));

export interface DotPager {
  /** Slides in the row, counted from the DOM. */
  slideCount: number;
  /** Row can scroll — without it there is nothing to page through. */
  hasOverflow: boolean;
  /** How far along the row is: 0 at its start, 1 at its end. */
  progress: number;
  /** Attach to the scrolling element's onScroll. */
  handleScroll: () => void;
  /** Scroll to a fraction of the row's extent, for a dot click. */
  scrollToFraction: (fraction: number) => void;
}

/**
 * Scroll-position tracking for a horizontal row, for pager dots.
 *
 * Reports a fraction rather than a slide index, because the dots are a
 * position indicator, not a per-card list: three dots over twenty cards can
 * only mean thirds. Slide count still comes from the row's DOM children — a
 * placeholder fragment renders several cards from one element, so counting
 * React nodes would call three placeholders one slide — and is what tells a
 * short row to show a dot per card instead.
 *
 * Pass `enabled: false` on rows that show no dots — the observers are then
 * never wired up.
 */
export function useDotPager(
  ref: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  deps: React.DependencyList,
): DotPager {
  const [slideCount, setSlideCount] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [progress, setProgress] = useState(0);

  useOverflowMeasure(ref, () => {
    const el = ref.current;
    if (!el || !enabled) return;
    setSlideCount(el.children.length);
    const extent = el.scrollWidth - el.clientWidth;
    setHasOverflow(extent > 1);
    // Appending cards grows the extent without moving scrollLeft, so a row
    // sitting at its end would keep the last dot lit even though it is now
    // mid-row. Re-derive the fraction from the new extent.
    setProgress(extent > 0 ? clampFraction(el.scrollLeft / extent) : 0);
  }, [...deps, enabled]);

  const handleScroll = useCallback(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const extent = el.scrollWidth - el.clientWidth;
    setProgress(extent > 0 ? clampFraction(el.scrollLeft / extent) : 0);
  }, [ref, enabled]);

  const scrollToFraction = useCallback(
    (fraction: number) => {
      const el = ref.current;
      if (!el) return;
      const extent = el.scrollWidth - el.clientWidth;
      el.scrollTo({ left: extent * fraction, behavior: 'smooth' });
    },
    [ref],
  );

  return { slideCount, hasOverflow, progress, handleScroll, scrollToFraction };
}
